import { useRef, useState } from "react";
import { api } from "@/lib/api";
import { track } from "@/lib/track";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Upload, Loader2, AlertTriangle, X } from "lucide-react";

const STEPS = ["Identification", "Access & rights", "Metadata & identifiers", "File or URL", "Reviewer & confirm"];

const EMPTY = {
  title: "", source_type: "", provenance: "External published source", author_speaker: "", organisation: "",
  access_classification: "", copyright_note: "", date: "", geography: "", conference_journal: "", language: "English",
  doi: "", pmid: "", nct: "", abstract_no: "", internal_no: "", url: "",
  themes: "", assets: "Elacestrant (ORSERDU)", competitors: "", evidence_summary: "", reviewer: "", family_of: "",
};

export default function AddSourceWizard({ meta, onAdded }) {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(EMPTY);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [dupes, setDupes] = useState(null);
  const fileRef = useRef(null);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const stepValid = () => {
    if (step === 0) return form.title.trim() && form.source_type && form.provenance;
    if (step === 1) return form.access_classification && form.copyright_note.trim();
    if (step === 2) return form.date && form.geography.trim();
    return true;
  };

  const reset = () => { setForm(EMPTY); setFile(null); setStep(0); setDupes(null); };

  const submit = (opts = {}) => {
    const fd = new FormData();
    Object.entries(form).forEach(([k, v]) => fd.append(k, v));
    if (opts.draft) fd.append("draft", "true");
    if (opts.force) fd.append("force", "true");
    if (opts.family_of) fd.set("family_of", opts.family_of);
    if (file) fd.append("file", file);
    setBusy(true);
    api.post("/source-library/upload", fd).then((r) => {
      onAdded(r.data);
      track("source_uploaded", "/sources", { source_id: r.data.id });
      toast.success(opts.draft ? `Draft ${r.data.id} saved` : `Source ${r.data.id} submitted for processing — verification and scientific review pending`);
      reset(); setOpen(false);
    }).catch((e) => {
      const d = e.response?.data?.detail;
      if (e.response?.status === 409 && d?.duplicates) { setDupes(d.duplicates); return; }
      toast.error(typeof d === "string" ? d : "Upload failed — please retry.", { action: { label: "Retry", onClick: () => submit(opts) } });
    }).finally(() => setBusy(false));
  };

  const onDrop = (e) => {
    e.preventDefault();
    const f = e.dataTransfer?.files?.[0];
    if (f) setFile(f);
  };

  const sel = (k, placeholder, options, tid) => (
    <Select value={form[k]} onValueChange={set(k)}>
      <SelectTrigger className="text-sm" data-testid={tid} aria-label={placeholder}><SelectValue placeholder={placeholder} /></SelectTrigger>
      <SelectContent className="max-h-72">{options.map((o) => <SelectItem key={o} value={o} className="text-xs">{o}</SelectItem>)}</SelectContent>
    </Select>
  );

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
      <DialogTrigger asChild>
        <Button className="h-8 bg-teal-600 hover:bg-teal-700 text-white text-xs" data-testid="add-source-btn">
          <Plus className="w-3.5 h-3.5 mr-1" />Add source
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto" aria-describedby="add-source-desc">
        <DialogHeader>
          <DialogTitle className="font-heading">Add source — governed intake workflow</DialogTitle>
          <p id="add-source-desc" className="text-xs text-slate-500">Four-step governed intake: source type, metadata, document or link, then classification and duplicate check.</p>
        </DialogHeader>
        <div className="flex flex-wrap items-center gap-1 mb-1" role="list" aria-label="Intake progress">
          {STEPS.map((sName, i) => (
            <button key={sName} onClick={() => i < step && setStep(i)} data-testid={`wizard-step-${i}`} role="listitem"
              aria-current={i === step ? "step" : undefined} aria-label={`Step ${i + 1}: ${sName}${i < step ? " (completed, click to return)" : i === step ? " (current)" : ""}`}
              className={`text-[9.5px] font-mono uppercase tracking-wide rounded-full px-2 py-0.5 border ${i === step ? "bg-slate-900 text-white border-slate-900" : i < step ? "bg-teal-50 text-teal-700 border-teal-200" : "bg-slate-50 text-slate-400 border-slate-200"}`}>
              {i + 1}. {sName}
            </button>
          ))}
        </div>

        {dupes && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3" data-testid="duplicate-warning">
            <p className="text-xs font-semibold text-amber-800 flex items-center gap-1.5"><AlertTriangle className="w-3.5 h-3.5" />Potential duplicate source detected</p>
            <ul className="mt-1.5 space-y-0.5">{dupes.map((d) => <li key={d.id} className="text-[11px] text-amber-700"><span className="font-mono">{d.id}</span> — {d.title}</li>)}</ul>
            <div className="flex flex-wrap gap-1.5 mt-2">
              <Button variant="outline" className="h-7 text-[10.5px]" onClick={() => submit({ force: true, family_of: dupes[0].id })} data-testid="dupe-link-family">Link to same evidence family</Button>
              <Button variant="outline" className="h-7 text-[10.5px]" onClick={() => submit({ force: true })} data-testid="dupe-distinct">Confirm genuinely distinct</Button>
              <Button variant="outline" className="h-7 text-[10.5px]" onClick={() => setDupes(null)}>Back to form</Button>
              <Button variant="outline" className="h-7 text-[10.5px] text-red-700 border-red-200" onClick={() => { reset(); setOpen(false); }}>Cancel ingestion</Button>
            </div>
          </div>
        )}

        {!dupes && step === 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="sm:col-span-2"><Input placeholder="Title *" value={form.title} onChange={set("title")} data-testid="wiz-title" className="text-sm" /></div>
            {sel("source_type", "Source type *", meta.source_types, "wiz-type")}
            {sel("provenance", "Provenance *", meta.provenances, "wiz-provenance")}
            <Input placeholder="Author or speaker" value={form.author_speaker} onChange={set("author_speaker")} className="text-sm" />
            <Input placeholder="Organisation" value={form.organisation} onChange={set("organisation")} className="text-sm" />
          </div>
        )}

        {!dupes && step === 1 && (
          <div className="grid grid-cols-1 gap-3">
            {sel("access_classification", "Access classification *", meta.access_classifications, "wiz-access")}
            <Input placeholder="Copyright / usage note * (owner, permitted use, quotation permission)" value={form.copyright_note} onChange={set("copyright_note")} data-testid="wiz-copyright" className="text-sm" />
            <p className="text-[10px] text-slate-400">Restricted, confidential or client-provided documents are visible only to authorised users. Do not upload content obtained by bypassing paywalls, unauthorised recording or private conversations.</p>
          </div>
        )}

        {!dupes && step === 2 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div><p className="text-[10px] text-slate-400 mb-0.5">Publication / observation date *</p>
              <Input type="date" value={form.date} onChange={set("date")} data-testid="wiz-date" className="text-sm" /></div>
            <div><p className="text-[10px] text-slate-400 mb-0.5">Country or global relevance *</p>
              <Input placeholder="e.g., Global / Europe (Germany)" value={form.geography} onChange={set("geography")} data-testid="wiz-geography" className="text-sm" /></div>
            <Input placeholder="Journal or congress" value={form.conference_journal} onChange={set("conference_journal")} className="text-sm" />
            <Input placeholder="Language" value={form.language} onChange={set("language")} className="text-sm" />
            <Input placeholder="DOI (optional)" value={form.doi} onChange={set("doi")} data-testid="wiz-doi" className="text-sm" />
            <Input placeholder="PMID (optional)" value={form.pmid} onChange={set("pmid")} className="text-sm" />
            <Input placeholder="NCT number (optional)" value={form.nct} onChange={set("nct")} className="text-sm" />
            <Input placeholder="Abstract / internal doc no. (optional)" value={form.abstract_no} onChange={set("abstract_no")} className="text-sm" />
            <Input placeholder="Clinical themes (comma-separated)" value={form.themes} onChange={set("themes")} className="text-sm" />
            <Input placeholder="Asset" value={form.assets} onChange={set("assets")} className="text-sm" />
            <div className="sm:col-span-2"><Input placeholder="Competitors (comma-separated)" value={form.competitors} onChange={set("competitors")} className="text-sm" /></div>
            {form.date && <p className="sm:col-span-2 text-[10px] text-slate-500">Cut-off classification: <span className={form.date <= "2026-06-14" ? "text-teal-700 font-semibold" : "text-amber-700 font-semibold"}>{form.date <= "2026-06-14" ? "Eligible — on or before cut-off (2026-06-14)" : "Post-cut-off — excluded from current assessment"}</span></p>}
          </div>
        )}

        {!dupes && step === 3 && (
          <div className="space-y-3">
            <Input placeholder="URL to legitimate original source" value={form.url} onChange={set("url")} data-testid="wiz-url" className="text-sm" />
            <div onDragOver={(e) => e.preventDefault()} onDrop={onDrop}
              className="border-2 border-dashed border-slate-300 rounded-lg p-5 text-center cursor-pointer hover:border-teal-400"
              onClick={() => fileRef.current?.click()} data-testid="wiz-dropzone">
              <Upload className="w-5 h-5 text-slate-400 mx-auto mb-1.5" />
              {file ? (
                <p className="text-xs text-teal-700 font-semibold flex items-center justify-center gap-2">{file.name} ({(file.size / 1024).toFixed(0)} KB)
                  <button onClick={(e) => { e.stopPropagation(); setFile(null); }} className="text-red-500" aria-label="Remove selected file" data-testid="wiz-remove-file"><X className="w-3.5 h-3.5" aria-hidden="true" /></button></p>
              ) : (
                <p className="text-xs text-slate-500">Drag & drop or click — PDF, Word, Excel or text · max 25 MB · files are security-scanned before processing</p>
              )}
              <input ref={fileRef} type="file" accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,.csv,.md,.rtf" className="hidden"
                onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="wiz-file-input" />
            </div>
            <Textarea placeholder="Evidence summary (optional)" value={form.evidence_summary} onChange={set("evidence_summary")} className="text-sm min-h-[60px]" />
          </div>
        )}

        {!dupes && step === 4 && (
          <div className="space-y-3">
            <Input placeholder="Assign reviewer (e.g., Example MarksMan reviewer A)" value={form.reviewer} onChange={set("reviewer")} data-testid="wiz-reviewer" className="text-sm" />
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 grid grid-cols-2 gap-x-4 gap-y-1.5 text-[11px] text-slate-600" data-testid="wiz-confirm-summary">
              {[["Title", form.title], ["Source type", form.source_type], ["Provenance", form.provenance], ["Access", form.access_classification],
                ["Date", form.date], ["Geography", form.geography], ["DOI", form.doi || "—"], ["File", file ? file.name : form.url || "—"], ["Reviewer", form.reviewer || "Unassigned"]].map(([l, v]) => (
                <p key={l}><span className="font-mono text-[9px] uppercase text-slate-400 block">{l}</span>{v}</p>
              ))}
            </div>
            <p className="text-[10px] text-amber-700 flex items-start gap-1"><AlertTriangle className="w-3 h-3 shrink-0 mt-0.5" />
              The source will be registered as Verification pending / Not reviewed. Upload never implies verification, scientific review or approval for synthesis.</p>
          </div>
        )}

        {!dupes && (
          <div className="flex items-center justify-between gap-2 mt-2">
            <Button variant="outline" className="h-8 text-xs" onClick={() => (step === 0 ? setOpen(false) : setStep(step - 1))}>{step === 0 ? "Cancel" : "Back"}</Button>
            <div className="flex gap-2">
              {step === 4 && <Button variant="outline" className="h-8 text-xs" disabled={busy || !form.title} onClick={() => submit({ draft: true })} data-testid="wiz-save-draft">Save as draft</Button>}
              {step < 4 ? (
                <Button className="h-8 bg-slate-900 hover:bg-slate-800 text-white text-xs" disabled={!stepValid()} onClick={() => setStep(step + 1)} data-testid="wiz-next">Next</Button>
              ) : (
                <Button className="h-8 bg-teal-600 hover:bg-teal-700 text-white text-xs" disabled={busy} onClick={() => submit()} data-testid="wiz-submit">
                  {busy && <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />}Submit for processing
                </Button>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
